import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  BsrsGrid,
  Column,
  commandColumnProps,
  createLookupFilterCell,
  LookupCell
} from '@/components/BsrsGrid'
import Resx from '@/components/Resx'
import { BoxHeader, Main, MainBody, MainTop, MainTopLeft, MainTopTitle, WhiteBox } from '@/components/Layout'
import context from '@/context'
import { CreateChangeItem } from '@/components/changeRequests/createChangeItem'
import cached from '@/internal-app/cached'
import NameColumn from '@/components/BsrsGrid/BsrsGrid.NameColumn'
import { useLocalization } from '@/hooks/localization'
import { useDispatch, useSelector } from 'react-redux'
import { activeTaskSelector } from '@/components/changeRequests/selectors'
import { actionCreators as formsActionCreators, getUid } from '@/store/Forms'
import { actionCreators as settingsActionCreators } from '@/store/settings'
import getComponentKey from '@/utils/getComponentKey'
import { InfoContent, InfoLabel, InfoRow } from '@/components/InfoRow'
import LoadingIndicator from '@/components/LoadingIndicator'
import { useSignalR } from '@/hooks/useSignalR'
import { getStore } from '@/store'
import { actionCreators as activeTaskActions } from '@/store/myActiveTask'
import { personTypes } from '@/enums/PersonTypes'

const CompanyList = React.memo((props) => {
  const { localize, locale } = useLocalization()
  let activeTask = useSelector(activeTaskSelector)
  const isAssignMergeCompaniesTask = activeTask && activeTask.type && activeTask.type.code === 'AssignMergedCompanies'
  const isManageMergeCompaniesTask = activeTask && activeTask.type && activeTask.type.code === 'ManageMergedCompanies'
  const dispatch = useDispatch()
  const settings = useSelector((state) => state.settings.settings)
  const [gridKey, setGridKey] = useState(getComponentKey())
  const [disabledButtons, setDisabledButtons] = useState({}) // Per-company button disabled state
  const hasDispatched = useRef(false)
  const [isLoading, setIsLoading] = useState(false)

  const parseModifiedData = useCallback(() => {
    const modifiedData = activeTask.changeRequest.items[0].modifiedData

    try {
      const parsedData = JSON.parse(modifiedData) || {}
      return { ...parsedData }
    } catch (error) {
      console.error('Error parsing modifiedData:', error)
      return {}
    }
  }, [activeTask])

  const parseOriginalData = useCallback(() => {
    const originalData = activeTask.changeRequest.items[0].originalData
    try {
      const parsedData = JSON.parse(originalData) || {}
      return { ...parsedData }
    } catch (error) {
      console.error('Error parsing originalData:', error)
      return {}
    }
  }, [activeTask])

  // State to hold parsed modified data
  const mergedCompanyCountDB = isAssignMergeCompaniesTask ? (parseModifiedData() && parseModifiedData().mergedCompanyCount != null ? parseModifiedData().mergedCompanyCount : 0) : 0
  const [parsedCompanies, setParsedCompanies] = useState([])
  const [mergedCompaniesCount, setMergedCompaniesCount] = useState(parsedCompanies.length + mergedCompanyCountDB)

  useSignalR('activeTaskChangedOnBE', () => {
    activeTask = getStore().dispatch(activeTaskActions.getMyActiveTask())
    const companies = parseMergedCompanies()
    setParsedCompanies(companies)
    setMergedCompaniesCount(parsedCompanies.length + mergedCompanyCountDB)
  }, [])

  const parseMergedCompanies = useCallback(() => {
    const modifiedData = parseModifiedData()
    return modifiedData.mergedCompanies ? [ ...modifiedData.mergedCompanies ] : []
  }, [parseModifiedData])

  useEffect(() => {
    setIsLoading(true)
    if (activeTask && activeTask.changeRequest && activeTask.changeRequest.items[0]) {
      // Call to update change items to trigger validations
      if (!hasDispatched.current && isAssignMergeCompaniesTask) {
        const uid = getUid('companies', activeTask.changeRequest.items[0].id, '~')
        const parsedModifiedData = parseModifiedData()
        const parsedOrigData = parseOriginalData()
        const changeItemWithParsedModifiedData = {
          ...activeTask.changeRequest.items[0],
          modifiedData: { ...parsedModifiedData },
          originalData: { ...parsedOrigData }
        }
        dispatch(formsActionCreators.dataLoaded(uid, changeItemWithParsedModifiedData))
        // Dispatch only once
        dispatch(formsActionCreators.change(uid, '', parsedModifiedData, [], () => {
          setIsLoading(false)
        }))
        // Mark dispatch as done
        hasDispatched.current = true
      }

      const companies = parseMergedCompanies()
      setParsedCompanies(companies)

      // Load the state of disabled buttons based on parsed companies
      const initialDisabledState = {}
      companies.forEach(company => {
        initialDisabledState[company.id] = true
      })
      setDisabledButtons(initialDisabledState)

      // Update the merged companies count
      setMergedCompaniesCount(companies.length + mergedCompanyCountDB)
    }
    setGridKey(getComponentKey)
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask])

  const createRelatedPerson = (dataItem, currentCount, relatedTypes) => {
    // Find Merged companies type
    const selectedType = relatedTypes.find(item => item.code === 'Mrgdcmp')
    if (!selectedType) {
      throw new Error('Related person type with code "Mrgdcmp" not found')
    }
    return {
      __isCreated: true,
      personType: personTypes.enum.Institution,
      __isModified: true,
      institution: {
        id: dataItem.personId,
        name: dataItem.name,
        latinName: dataItem.latinName
      },
      relatedPersonType: selectedType,
      validFrom: new Date().toISOString().split('T')[0],
      id: -1 - currentCount
    }
  }

  const handleAddToMerge = useCallback((dataItem) => {
    setIsLoading(true)
    setDisabledButtons(prevState => ({ ...prevState, [dataItem.id]: true }))
    const uid = getUid('companies', activeTask.changeRequest.items[0].id, '~')
    const parsedModifiedData = parseModifiedData()
    const parsedOrigData = parseOriginalData()
    const changeItemWithParsedModifiedData = {
      ...activeTask.changeRequest.items[0],
      modifiedData: { ...parsedModifiedData },
      originalData: { ...parsedOrigData }
    }
    dispatch(formsActionCreators.dataLoaded(uid, changeItemWithParsedModifiedData))
    const updatedCompanies = [...parsedModifiedData, ...parsedCompanies, dataItem]
    dispatch(formsActionCreators.change(uid, 'mergedCompanies', updatedCompanies, [], () => {
      setIsLoading(false)
    }))
    setParsedCompanies(updatedCompanies)

    const existingRelatedPersons = parsedModifiedData.associatedToInstitutions || []
    const currentCount = existingRelatedPersons.length

    cached.getRelatedPersonTypes().then(relatedTypes => {
      const relatedPerson = createRelatedPerson(dataItem, currentCount, relatedTypes)
      const updatedRelatedPersons = [...existingRelatedPersons, relatedPerson]
      dispatch(
        formsActionCreators.change(uid, 'associatedToInstitutions', updatedRelatedPersons, [], () => {
          setIsLoading(false)
        })
      )
    }).catch(error => console.error('Error creating merge relation: ', error))
  }, [activeTask, parseModifiedData, dispatch, parsedCompanies, parseOriginalData])

  const handleRemoveFromMerge = useCallback((dataItem) => {
    setIsLoading(true)

    const uid = getUid('companies', activeTask.changeRequest.items[0].id, '~')
    const parsedModifiedData = parseModifiedData()
    const parsedOrigData = parseOriginalData()
    const changeItemWithParsedModifiedData = {
      ...activeTask.changeRequest.items[0],
      modifiedData: { ...parsedModifiedData },
      originalData: { ...parsedOrigData }
    }
    dispatch(formsActionCreators.dataLoaded(uid, changeItemWithParsedModifiedData))

    const filteredCompanies = parsedCompanies.filter(company => company.id !== dataItem.id)
    const updatedCompanies = [...parsedOrigData, ...filteredCompanies]
    dispatch(formsActionCreators.change(uid, 'mergedCompanies', updatedCompanies, [], () => {
      setIsLoading(false)
    }))

    // Remove the related person corresponding to the removed company
    const existingRelatedPersons = parsedModifiedData.associatedToInstitutions || []
    const updatedRelatedPersons = existingRelatedPersons.filter(
      relatedPerson => relatedPerson.institution && relatedPerson.institution.id !== dataItem.personId)

    dispatch(formsActionCreators.change(uid, 'associatedToInstitutions', updatedRelatedPersons, [], () => {
      setIsLoading(false)
    }))
    setParsedCompanies(updatedCompanies) // Update state with new data
  }, [activeTask, parseModifiedData, dispatch, parsedCompanies, parseOriginalData])

  useEffect(() => {
    // After the grid key changes (indicating grid update), re-enable the plus button for all companies
    setDisabledButtons(prevState => {
      const newState = { ...prevState }
      parsedCompanies.forEach(company => {
        newState[company.id] = true // Keep buttons disabled for companies still in the list
      })
      return newState
    })
  }, [gridKey, parsedCompanies])

  useEffect(() => {
    if (!settings || !Object.keys(settings).includes('companyMergeWhitelist')) {
      setIsLoading(true)
      dispatch(settingsActionCreators.getWhitelistSettings())
        .then(() => {
          setIsLoading(false)
        })
        .catch(error => {
          console.error('Error fetching whitelist settings:', error)
          setIsLoading(false)
        })
    }
  }, [dispatch, settings, activeTask])

  const addMergeButton = (dataItem) => isAssignMergeCompaniesTask ? [
    {
      icon: 'plus',
      onClick: () => {
        handleAddToMerge(dataItem)
      },
      title: 'Add to merge',
      disabled: disabledButtons[dataItem.id]
    }
  ] : null

  const removeFromMergeButton = (dataItem) => isAssignMergeCompaniesTask ? [
    {
      icon: 'minus',
      onClick: () => {
        handleRemoveFromMerge(dataItem)
      },
      title: 'Remove from merge'
    }
  ] : null

  const getDetailUrl = (dataItem, shouldRender) => {
    if (shouldRender) {
      return `/companies/${dataItem.id}`
    }
    return null
  }

  const filterTypeCell = createLookupFilterCell(cached.getCompanyTypes, {
    filter: (data) => {
      // Apply filtering logic based on settings.companyMergeWhitelist
      return data.filter(type => settings.companyMergeWhitelist.includes(type.id))
    }
  })

  const filterStatusCell = createLookupFilterCell(cached.getCompanyStates, {
    filter: (data) => {
      return data.filter(status => settings.companyMergeAllowedStates.includes(status.id))
    }
  })

  if ((!settings || !Object.keys(settings).includes('companyMergeWhitelist') || isLoading) && activeTask) {
    return <LoadingIndicator />
  }
  return (
    <Main>
      <MainTop>
        <MainTopTitle>
          <Resx name='Institutions.CompaniesTitle' />
        </MainTopTitle>
        <MainTopLeft>
          <CreateChangeItem
            buttonText='Institutions.CreateNewCompany'
            scope='Companies'
            additionalPermission='CanCreateCompanies' />
        </MainTopLeft>
      </MainTop>
      <MainBody>
        {isAssignMergeCompaniesTask &&
        <React.Fragment>
          <BoxHeader>
                Assign Companies to Merge
          </BoxHeader>
          <WhiteBox>
            <InfoRow>
              <InfoLabel size='xl'>Merged companies count (including past merges): </InfoLabel><InfoContent>{mergedCompaniesCount}</InfoContent>
            </InfoRow>
          </WhiteBox>
        </React.Fragment>
        }
        {isManageMergeCompaniesTask &&
        <React.Fragment>
          <BoxHeader>
                Manage Merge Companies
          </BoxHeader>
        </React.Fragment>
        }
        {isAssignMergeCompaniesTask &&
        <div style={{ marginBottom: '30px' }}>
          <BsrsGrid persistenceId={'companiesMerge'} dataSource={context.api.changeRequestService.getMergeCompanies} lastReloadedAt={gridKey}>
            {NameColumn({ locale, title: localize('Common.EnglishName'), editable: false })}
            <Column field='code' title={localize('Common.Code')} width='120px' />
            <Column field='type' title={localize('Common.Type')} cell={LookupCell} filterCell={createLookupFilterCell(cached.getCompanyTypes)} />
            <Column field='subtype' title={localize('Common.Subtype')} cell={LookupCell} filterCell={createLookupFilterCell(cached.getCompanySubtypes)} />
            <Column field='status' title={localize('Common.Status')} cell={LookupCell} width='120px' filterCell={isAssignMergeCompaniesTask ? filterStatusCell : createLookupFilterCell(cached.getCompanyStates)} />
            <Column field='class' title={localize('Institutions.CompanyClass')} cell={LookupCell} filterCell={createLookupFilterCell(cached.getCompanyClasses)} />
            <Column field='mergedCompanyCount' title={localize('Institutions.MergedCompanyCount')} filter='numeric' />
            <Column {...commandColumnProps({ detailUrl: x => getDetailUrl(x, !isAssignMergeCompaniesTask),
              additionalButtons: (dataItem) => removeFromMergeButton(dataItem) })} />
          </BsrsGrid>
        </div>}
        <BsrsGrid persistenceId={'companies'} dataSource={isAssignMergeCompaniesTask ? context.api.companyService.getCompaniesForMerge : isManageMergeCompaniesTask ? context.api.companyService.getParentMergedCompanies : context.api.companyService.query} lastReloadedAt={gridKey}>
          {NameColumn({ locale, title: localize('Common.EnglishName'), editable: false })}
          <Column field='code' title={localize('Common.Code')} width='120px' />
          <Column field='type' title={localize('Common.Type')} cell={LookupCell}
            filterCell={isAssignMergeCompaniesTask ? filterTypeCell : createLookupFilterCell(cached.getCompanyTypes)} />
          <Column field='subtype' title={localize('Common.Subtype')} cell={LookupCell}
            filterCell={createLookupFilterCell(cached.getCompanySubtypes)} />
          <Column field='status' title={localize('Common.Status')} cell={LookupCell} width='120px'
            filterCell={isAssignMergeCompaniesTask ? filterStatusCell : createLookupFilterCell(cached.getCompanyStates)} />
          <Column field='class' title={localize('Institutions.CompanyClass')} cell={LookupCell}
            filterCell={createLookupFilterCell(cached.getCompanyClasses)} />
          <Column field='mergedCompanyCount' title={localize('Institutions.MergedCompanyCount')} filter='numeric' />
          <Column {...commandColumnProps({ detailUrl: (x) => getDetailUrl(x, !isAssignMergeCompaniesTask),
            additionalButtons: dataItem => addMergeButton(dataItem) })} />
        </BsrsGrid>
      </MainBody>
    </Main>
  )
})

export { CompanyList }



// WEBPACK FOOTER //
// ./src/internal-app/pages/companies/list.js